import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TableCell, TableRow, Typography } from "@mui/material";

import TruncateText from "../../../components/TruncateText";
import Filters from "../../../components/Filters";
import ListFilter from "../../../components/Filters/ListFilter";
import DateRangeFilter from "../../../components/Filters/DateRangeFilter";
import SortList from "../../../components/Filters/SortList";
import SearchInput from "../../../components/SearchInput";
import Table from "../../../components/Table";
import Pagination from "../../../components/Pagination";
import EmptyState from "../../../components/EmptyState";
import Tabs from "../../../components/Tabs";
import { approvedProductsListRequest } from "../../../store/Products/actions";
import messages from "../../../assets/locale/messages";
import emptyBoxImg from "../../../assets/images/placeholders/emptyBox.svg";
import noRequestImg from "../../../assets/images/placeholders/noProductImagePlacholder.svg";
import {
  areAllParamsEmpty,
  formatDate
} from "../../../utils/Helpers";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import {
  PRODUCT_STATUS,
  PRODUCT_STATUS_IDS,
  SORT_PRODUCTS
} from "../../../utils/Constants";
import {
  subCategoriesLookupRequest,
  categoriesLookupRequest,
  countriesLookupRequest,
  companiesLookupRequest
} from "../../../store/Lookups/actions";
import { showHideLoader } from "../../../store/Loader/actions";
import "../Products.scss";

const ApprovedProducts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoading = useSelector((state) => state.loader.showLoader);
  const lang = useSelector((state) => state.locale.lang);
  const { products, shared } = messages[lang];
  const productsList = useSelector(
    (state) => state.products.approvedProducts.list
  );
  const productsMeta = useSelector(
    (state) => state.products.approvedProducts.meta
  );
  const categoriesLookup = useSelector(
    (state) => state.lookups.categories.list
  );
  const subCategoriesLookup = useSelector(
    (state) => state.lookups.subCategories.list
  );

  const countriesLookup = useSelector(
    (state) => state.lookups.countries.list
  );

  const companiesLookup = useSelector(
    (state) => state.lookups.companies.list
  );

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilterValue, setStatusFilterValue] = useState([
    PRODUCT_STATUS_IDS.find(
      (status) => status.value === PRODUCT_STATUS.published
    ).id
  ]);
  const [categoryFilterValue, setCategoryFilterValue] = useState([]);
  const [subCategoryFilterValue, setSubCategoryFilterValue] =
    useState([]);
  const [countryFilterValue, setCountryFilterValue] = useState([]);
  const [dateFilterValue, setDateFilterValue] = useState({
    from: null,
    to: null
  });
  const [companiesFilterValue, setCompaniesFilterValue] = useState(
    []
  );
  const [sortingRange, setSortingRange] = useState(SORT_PRODUCTS[0]);
  const [clearFilters, setClearFilters] = useState(false);
  const [isApplyFilters, setIsApplyFilters] = useState(false);
  const [paramsObject, setParamsObject] = useState({
    Category_ids: [],
    SubCategory_ids: [],
    country_ids: [],
    date_range_from: null,
    date_range_to: null,
    company_ids: []
  });

  useEffect(() => {
    dispatch(categoriesLookupRequest());
    dispatch(subCategoriesLookupRequest());
    dispatch(countriesLookupRequest());
    dispatch(companiesLookupRequest());
  }, []);

  useEffect(() => {
    dispatch(
      subCategoriesLookupRequest({ categoryId: categoryFilterValue })
    );
  }, [categoryFilterValue]);

  useEffect(() => {
    dispatch(showHideLoader(true));
    let statusValue = null;
    switch (tabValue) {
      case 0:
        statusValue = PRODUCT_STATUS_IDS.find(
          (status) => status.value === PRODUCT_STATUS.published
        ).id;
        break;
      case 1:
        statusValue = PRODUCT_STATUS_IDS.find(
          (status) => status.value === PRODUCT_STATUS.unpublished
        ).id;
        break;
      case 2:
        statusValue = PRODUCT_STATUS_IDS.find(
          (status) => status.value === PRODUCT_STATUS.deleted
        ).id;
        break;
      default:
        break;
    }
    setClearFilters(true);
    setSearchValue("");
    setPage(1);
    setStatusFilterValue([statusValue]);
  }, [tabValue]);

  useEffect(() => {
    if (clearFilters) {
      setCategoryFilterValue([]);
      setSubCategoryFilterValue([]);
      setCountryFilterValue([]);
      setDateFilterValue({
        from: null,
        to: null
      });
      setCompaniesFilterValue([]);
      setClearFilters(false);
      setParamsObject({
        Category_ids: [],
        SubCategory_ids: [],
        country_ids: [],
        date_range_from: null,
        date_range_to: null,
        company_ids: []
      });
    }
  }, [clearFilters]);

  const requestProductsList = () => {
    dispatch(
      approvedProductsListRequest({
        params: {
          page,
          status_ids: statusFilterValue,
          search_value: searchValue,
          sort_id: sortingRange.value,
          ...paramsObject
        }
      })
    );
  };

  useEffect(() => {
    requestProductsList();
  }, [page, searchValue]);

  useEffect(() => {
    page > 1 ? setPage(1) : requestProductsList();
  }, [paramsObject]);

  useEffect(() => {
    page > 1 ? setPage(1) : requestProductsList();
  }, [sortingRange]);

  useEffect(() => {
    if (isApplyFilters) {
      setParamsObject({
        Category_ids: categoryFilterValue,
        SubCategory_ids: subCategoryFilterValue,
        country_ids: countryFilterValue,
        date_range_from: dateFilterValue?.from || null,
        date_range_to: dateFilterValue?.to || null,
        company_ids: companiesFilterValue
      });
      setIsApplyFilters(false);
    }
  }, [isApplyFilters]);

  useEffect(() => {
    setParamsObject({
      Category_ids: [],
      SubCategory_ids: [],
      country_ids: [],
      date_range_from: null,
      date_range_to: null,
      company_ids: []
    });
  }, [statusFilterValue]);

  const onImageError = (e) => {
    e.target.src = noRequestImg;
  };

  const renderProductTableRow = ({
    Id,
    DefaultImage,
    Name,
    CategoryName,
    SubCategoryName,
    ExporterName,
    Country,
    PublishedDate,
    DeletedDate
  }) => (
    <TableRow key={`request-row-${Id}`}>
      <TableCell>
        <div
          className="d-flex align-items-center gap-3 pointer"
          onClick={() =>
            navigate(
              ROUTE_PATHS.productDetailsApproved.replace(":id", Id)
            )
          }>
          <img
            src={DefaultImage || noRequestImg}
            onError={onImageError}
            alt="productImage"
            className="product-img border-radius-8"
          />
          <TruncateText text={Name || "__"} />
        </div>
      </TableCell>
      <TableCell>{CategoryName || "__"}</TableCell>
      <TableCell>{SubCategoryName || "__"}</TableCell>
      <TableCell>{ExporterName || "__"}</TableCell>
      <TableCell>{Country || "__"}</TableCell>
      {(tabValue === 0 || tabValue === 1) && (
        <TableCell>
          {PublishedDate ? formatDate(PublishedDate) : "__"}
        </TableCell>
      )}
      {tabValue === 2 && (
        <TableCell>
          {DeletedDate ? formatDate(DeletedDate) : "__"}
        </TableCell>
      )}
    </TableRow>
  );

  const dateColumnHeaders = [
    products.list.tableHeader.publishDate,
    products.list.tableHeader.unpublishDate,
    products.list.tableHeader.deleteDate
  ];

  const renderTabContent = () => (
    <div>
      <div className="table-header my-4">
        <SearchInput
          name="approved-products-search"
          id="approved-products-search"
          className="approved-products-search"
          value={searchValue}
          inputWrapperClass="search-input-wrapper search-input-wrapper-width small-placeholder-search"
          placeholder={products.list.searchPlaceholder}
          onChange={(value) => {
            setPage(1);
            setSearchValue(value);
          }}
        />
      </div>
      <div className="table-content">
        {productsList?.length ? (
          <Table
            className={"px-0"}
            headlines={[
              products.list.tableHeader.product,
              products.list.tableHeader.category,
              products.list.tableHeader.subCategory,
              products.list.tableHeader.companyName,
              products.list.tableHeader.companyCountry,
              dateColumnHeaders[tabValue]
            ]}
            rows={
              !isLoading &&
              productsList &&
              productsList.map((request) =>
                renderProductTableRow(request)
              )
            }
          />
        ) : (
          <div>
            {!isLoading && (
              <EmptyState
                subTitle={
                  !!searchValue || !areAllParamsEmpty(paramsObject)
                    ? shared.noSearchResults
                    : products.list.noProductsFound
                }
                className="empty-state-container"
                imgSrc={emptyBoxImg}
              />
            )}
          </div>
        )}
      </div>
      <div className="Pagination-container d-flex justify-content-center py-3 mt-2">
        {!isLoading && productsList?.length > 0 && (
          <Pagination
            count={productsMeta?.pages}
            page={productsMeta?.currentPage || page}
            handleChange={(page) => {
              setPage(page);
            }}
            defaultPage={1}
          />
        )}
      </div>
    </div>
  );

  const renderFilterContent = () => (
    <div className="filter-content-container h-100">
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={categoriesLookup}
          name="categories"
          label={products.list.filters.categories}
          selectedOptionsIds={categoryFilterValue}
          setSelectedOptionsIds={setCategoryFilterValue}
          clearFilters={clearFilters}
          collabsible
          isApplyFilters={isApplyFilters}
          showSearchBar
          searchPlaceholder={
            products.list.filters.searchPlaceholders.categories
          }
          filterModalOpen={filterModalOpen}
        />
      </div>
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={subCategoriesLookup}
          name="sub categories"
          label={products.list.filters.subCategories}
          searchPlaceholder={
            products.list.filters.searchPlaceholders.subCategories
          }
          placeholder={
            !!categoryFilterValue.length
              ? products.list.filters.searchPlaceholders
                  .noSubcategories
              : ""
          }
          selectedOptionsIds={subCategoryFilterValue}
          setSelectedOptionsIds={setSubCategoryFilterValue}
          clearFilters={clearFilters}
          collabsible
          showSearchBar
          isApplyFilters={isApplyFilters}
          filterModalOpen={filterModalOpen}
        />
      </div>
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={companiesLookup}
          name="companies"
          label={products.list.filters.companies}
          searchPlaceholder={
            products.list.filters.searchPlaceholders.companies
          }
          selectedOptionsIds={companiesFilterValue}
          setSelectedOptionsIds={setCompaniesFilterValue}
          clearFilters={clearFilters}
          collabsible
          showSearchBar
          isApplyFilters={isApplyFilters}
          filterModalOpen={filterModalOpen}
        />
      </div>
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={countriesLookup}
          name="countries"
          label={products.list.filters.countries}
          searchPlaceholder={
            products.list.filters.searchPlaceholders.countries
          }
          selectedOptionsIds={countryFilterValue}
          setSelectedOptionsIds={setCountryFilterValue}
          clearFilters={clearFilters}
          collabsible
          showSearchBar
          isApplyFilters={isApplyFilters}
          filterModalOpen={filterModalOpen}
        />
      </div>
      <div className="py-3 px-md-5 px-3">
        <DateRangeFilter
          name="date-range"
          label={products.list.filters.dateRange}
          onRequestFiltering={setDateFilterValue}
          clearFilters={clearFilters}
          collabsible
          selectedDateFrom={dateFilterValue?.from}
          selectedDateTo={dateFilterValue?.to}
          addBottomMargin
        />
      </div>
    </div>
  );

  const tabsArray = [
    {
      label: products.list.tabs.published,
      content: renderTabContent()
    },
    {
      label: products.list.tabs.unpublished,
      content: renderTabContent()
    },
    {
      label: products.list.tabs.deleted,
      content: renderTabContent()
    }
  ];

  return (
    <div className="products-list-container">
      <div className="page-title pb-4 d-flex justify-content-between align-items-md-center align-items-start flex-column flex-md-row">
        <div className="title-count-container d-flex justify-content-center align-items-center">
          <Typography variant="h5" className="me-3 title-color">
            {products.productsList}
          </Typography>
        </div>
        <div className="title-buttons-container d-flex justify-content-center gap-0 gap-md-3 align-items-md-center align-items-start flex-column flex-md-row">
          <SortList
            options={SORT_PRODUCTS}
            onSelectOption={(selectedOption) =>
              setSortingRange(selectedOption)
            }
            defaultOption={0}
            selectedOption={sortingRange}
          />
          <Filters
            handleClearFilters={setClearFilters}
            handleApplyFilters={setIsApplyFilters}
            FilterContent={renderFilterContent()}
            setIsModalOpen={(isOpen) => setFilterModalOpen(isOpen)}
          />
        </div>
      </div>
      <div className="products-table-container bg-white d-flex flex-column w-100  p-4 bg-white border-radius-8 mb-4">
        <div className="tabs-section">
          <Tabs
            tabs={tabsArray}
            value={tabValue}
            handleChange={setTabValue}
            activeTabAdornment={
              isLoading ? null : (
                <span className="records-count faint-blue-bg border-radius-50 p-2">
                  {productsMeta?.count || 0}
                </span>
              )
            }
          />
        </div>
      </div>
    </div>
  );
};

export default ApprovedProducts;
